import dayjs from "dayjs"
import { create } from "zustand"

/**
 * Filter state for Billing pages
 * Used for filtering billing campaigns and transactions
 */
export interface BillingFilterState {
  order?: "ASC" | "DESC"
  acceptanceStatus?: "ALL" | "COMPLETED" | "PENDING" | "REJECTED"
  startTime?: string // YYYY-MM-DD format
  endTime?: string // YYYY-MM-DD format
  paidType?: "ALL" | "COMPLETED" | "PENDING"
}

interface BillingFilterStore {
  // State
  countFilter: number
  filterState: BillingFilterState

  // Actions
  setCountFilter: (count: number) => void
  updateFilterState: (newState: Partial<BillingFilterState>) => void
  setFilter: (filter: BillingFilterState) => void
  resetFilter: () => void
  calculateFilterCount: () => number
  recalculateFilterCount: () => void
}

const DEFAULT_FILTER_STATE: BillingFilterState = {
  order: "DESC",
  acceptanceStatus: "ALL",
  startTime: dayjs().startOf("month").format("YYYY-MM-DD"),
  endTime: dayjs().endOf("month").format("YYYY-MM-DD"),
  paidType: "ALL"
}

const createDefaultFilter = (): BillingFilterState => ({
  ...DEFAULT_FILTER_STATE
})

const calculateFilterCount = (state: BillingFilterState): number => {
  let count = 0

  // Count order if changed from default DESC
  if (state.order && state.order !== "DESC") {
    count++
  }

  // Count status filter if not ALL
  if (state.acceptanceStatus && state.acceptanceStatus !== "ALL") {
    count++
  }

  // Count paid type filter if not ALL
  if (state.paidType && state.paidType !== "ALL") {
    count++
  }
  return count
}

// Transform form data to API query params
export const transformBillingFilterToQueryParams = (formData: any): Partial<BillingFilterState> => {
  const params: Partial<BillingFilterState> = {}

  // Handle order
  if (formData.sortBy) {
    if (formData.sortBy === "NEWEST") {
      params.order = "DESC"
    } else if (formData.sortBy === "OLDEST") {
      params.order = "ASC"
    }
  }

  // Handle status
  if (formData.acceptanceStatus) {
    params.acceptanceStatus = formData.acceptanceStatus
  }

  // Handle paid type
  if (formData.paidType) {
    params.paidType = formData.paidType
  }

  return params
}

export const useFilterBilling = create<BillingFilterStore>()((set, get) => ({
  // Initial state
  countFilter: 0,
  filterState: createDefaultFilter(),

  // Actions
  setCountFilter: (count) => set({ countFilter: count }),

  updateFilterState: (newState) => {
    set((state) => {
      const updatedState = { ...state.filterState, ...newState }
      const count = calculateFilterCount(updatedState)
      return {
        filterState: updatedState,
        countFilter: count
      }
    })
  },

  setFilter: (filter) => {
    const count = calculateFilterCount(filter)
    set({
      filterState: { ...filter },
      countFilter: count
    })
  },

  resetFilter: () => {
    set({
      filterState: createDefaultFilter(),
      countFilter: 0
    })
  },

  calculateFilterCount: () => {
    const state = get()
    return calculateFilterCount(state.filterState)
  },

  recalculateFilterCount: () => {
    const state = get()
    const count = calculateFilterCount(state.filterState)
    set({ countFilter: count })
  }
}))
